import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { AppShell } from "./components/layout/AppShell";
import { LoginPage } from "./components/auth/LoginPage";
import { LoadingSpinner } from "./components/common/LoadingSpinner";
import { useEnsureUser } from "./hooks/useEnsureUser";
import { PublicShell } from "./components/public/PublicShell";
import { HomePage } from "./components/public/HomePage";
import { AdSenseLoader } from "./components/ads/AdSenseLoader";

const Dashboard = lazy(() =>
  import("./components/dashboard/Dashboard").then((module) => ({ default: module.Dashboard })),
);
const TipPage = lazy(() =>
  import("./components/tips/TipPage").then((module) => ({ default: module.TipPage })),
);
const TipHistory = lazy(() =>
  import("./components/tips/TipHistory").then((module) => ({ default: module.TipHistory })),
);
const LeaderboardPage = lazy(() =>
  import("./components/leaderboard/LeaderboardPage").then((module) => ({
    default: module.LeaderboardPage,
  })),
);
const CompetitionList = lazy(() =>
  import("./components/competitions/CompetitionList").then((module) => ({
    default: module.CompetitionList,
  })),
);
const CompetitionDetail = lazy(() =>
  import("./components/competitions/CompetitionDetail").then((module) => ({
    default: module.CompetitionDetail,
  })),
);
const CreateCompetition = lazy(() =>
  import("./components/competitions/CreateCompetition").then((module) => ({
    default: module.CreateCompetition,
  })),
);
const ProfilePage = lazy(() =>
  import("./components/profile/ProfilePage").then((module) => ({ default: module.ProfilePage })),
);
const AchievementsPage = lazy(() =>
  import("./components/achievements/AchievementsPage").then((module) => ({
    default: module.AchievementsPage,
  })),
);
const AnalyticsPage = lazy(() =>
  import("./components/analytics/AnalyticsPage").then((module) => ({
    default: module.AnalyticsPage,
  })),
);
const AdminPage = lazy(() =>
  import("./components/admin/AdminPage").then((module) => ({ default: module.AdminPage })),
);
const OnboardingPage = lazy(() =>
  import("./components/onboarding/OnboardingPage").then((module) => ({
    default: module.OnboardingPage,
  })),
);
const LadderPredictorPage = lazy(() =>
  import("./components/predictor/LadderPredictorPage").then((module) => ({
    default: module.LadderPredictorPage,
  })),
);
const PossiblePositionsPage = lazy(() =>
  import("./components/positions/PossiblePositionsPage").then((module) => ({
    default: module.PossiblePositionsPage,
  })),
);
const NewsPage = lazy(() =>
  import("./components/news/NewsPage").then((module) => ({ default: module.NewsPage })),
);
const NewsStoryPage = lazy(() =>
  import("./components/news/NewsStoryPage").then((module) => ({ default: module.NewsStoryPage })),
);
const PrivacyPage = lazy(() =>
  import("./components/public/PrivacyPage").then((module) => ({ default: module.PrivacyPage })),
);
const GameStatsPage = lazy(() =>
  import("./components/stats/GameStatsPage").then((module) => ({ default: module.GameStatsPage })),
);
const PlayerPage = lazy(() =>
  import("./components/stats/PlayerPage").then((module) => ({ default: module.PlayerPage })),
);
const ClubPage = lazy(() =>
  import("./components/stats/ClubPage").then((module) => ({ default: module.ClubPage })),
);
const PlayerStatsPage = lazy(() =>
  import("./components/stats/PlayerStatsPage").then((module) => ({
    default: module.PlayerStatsPage,
  })),
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth0();
  const { data: user, isLoading: userLoading } = useEnsureUser();
  const location = useLocation();

  if (isLoading || userLoading) return <LoadingSpinner />;
  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  // Onboarding redirect: null selectedSports means they haven't chosen yet
  const needsOnboarding = user && user.selectedSports === null;
  const onOnboardingPage = location.pathname === "/onboarding";

  if (needsOnboarding && !onOnboardingPage) {
    return <Navigate to="/onboarding" replace />;
  }
  if (!needsOnboarding && onOnboardingPage) {
    return <Navigate to="/tipping/dashboard" replace />;
  }

  return <>{children}</>;
}

function LegacyRedirect({
  getTo,
}: {
  getTo: (params: Readonly<Record<string, string | undefined>>) => string;
}) {
  const params = useParams();
  const { search, hash } = useLocation();

  return <Navigate to={`${getTo(params)}${search}${hash}`} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AdSenseLoader />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route element={<PublicShell />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:slug" element={<NewsStoryPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/ladder/predictor" element={<LadderPredictorPage />} />
            <Route path="/ladder/possible-finishes" element={<PossiblePositionsPage />} />
            <Route path="/ladder" element={<LegacyRedirect getTo={() => "/ladder/predictor"} />} />
            <Route
              path="/predictor"
              element={<LegacyRedirect getTo={() => "/ladder/predictor"} />}
            />
            <Route
              path="/possible-positions"
              element={<LegacyRedirect getTo={() => "/ladder/possible-finishes"} />}
            />
          </Route>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Routes>
                  <Route path="/onboarding" element={<OnboardingPage />} />
                  <Route
                    path="/*"
                    element={
                      <AppShell>
                        <Routes>
                          <Route path="/tipping/dashboard" element={<Dashboard />} />
                          <Route path="/tipping/rounds/:roundId" element={<TipPage />} />
                          <Route path="/tipping/games/:fixtureId" element={<GameStatsPage />} />
                          <Route path="/tipping/player-stats" element={<PlayerStatsPage />} />
                          <Route path="/tipping/players/:playerId" element={<PlayerPage />} />
                          <Route path="/tipping/clubs/:teamId" element={<ClubPage />} />
                          <Route path="/tipping/history" element={<TipHistory />} />
                          <Route path="/tipping/leaderboard" element={<LeaderboardPage />} />
                          <Route path="/tipping/competitions" element={<CompetitionList />} />
                          <Route path="/tipping/competitions/new" element={<CreateCompetition />} />
                          <Route path="/tipping/competitions/:id" element={<CompetitionDetail />} />
                          <Route path="/tipping/achievements" element={<AchievementsPage />} />
                          <Route path="/tipping/analytics" element={<AnalyticsPage />} />
                          <Route path="/profile" element={<ProfilePage />} />
                          <Route path="/admin" element={<AdminPage />} />
                          <Route
                            path="/tipping"
                            element={<LegacyRedirect getTo={() => "/tipping/dashboard"} />}
                          />

                          <Route
                            path="/dashboard"
                            element={<LegacyRedirect getTo={() => "/tipping/dashboard"} />}
                          />
                          <Route
                            path="/tips/:roundId"
                            element={
                              <LegacyRedirect
                                getTo={(params) => `/tipping/rounds/${params.roundId ?? ""}`}
                              />
                            }
                          />
                          <Route
                            path="/games/:fixtureId"
                            element={
                              <LegacyRedirect
                                getTo={(params) => `/tipping/games/${params.fixtureId ?? ""}`}
                              />
                            }
                          />
                          <Route
                            path="/players/:playerId"
                            element={
                              <LegacyRedirect
                                getTo={(params) => `/tipping/players/${params.playerId ?? ""}`}
                              />
                            }
                          />
                          <Route
                            path="/clubs/:teamId"
                            element={
                              <LegacyRedirect
                                getTo={(params) => `/tipping/clubs/${params.teamId ?? ""}`}
                              />
                            }
                          />
                          <Route
                            path="/history"
                            element={<LegacyRedirect getTo={() => "/tipping/history"} />}
                          />
                          <Route
                            path="/leaderboard"
                            element={<LegacyRedirect getTo={() => "/tipping/leaderboard"} />}
                          />
                          <Route
                            path="/competitions"
                            element={<LegacyRedirect getTo={() => "/tipping/competitions"} />}
                          />
                          <Route
                            path="/competitions/new"
                            element={<LegacyRedirect getTo={() => "/tipping/competitions/new"} />}
                          />
                          <Route
                            path="/competitions/:id"
                            element={
                              <LegacyRedirect
                                getTo={(params) => `/tipping/competitions/${params.id ?? ""}`}
                              />
                            }
                          />
                          <Route
                            path="/achievements"
                            element={<LegacyRedirect getTo={() => "/tipping/achievements"} />}
                          />
                          <Route
                            path="/analytics"
                            element={<LegacyRedirect getTo={() => "/tipping/analytics"} />}
                          />
                          <Route
                            path="/player-stats"
                            element={<LegacyRedirect getTo={() => "/tipping/player-stats"} />}
                          />
                          <Route path="*" element={<Navigate to="/tipping/dashboard" replace />} />
                        </Routes>
                      </AppShell>
                    }
                  />
                </Routes>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
